<template>
  <div class="page road-exclusion-form flex">
    <div class="form-panel">
      <h1 class="form-title">{{ isEdit ? 'Edit road exclusion' : 'Add road exclusion' }}</h1>

      <div class="section-title">Info</div>
      <div class="field">
        <label>Name</label>
        <input v-model="form.name" type="text" @input="onNameInput" />
      </div>
      <div class="field">
        <label>Segment ID</label>
        <input :value="form.segment_id" type="text" disabled />
      </div>
      <div class="field">
        <label>Segment length</label>
        <input :value="lengthLabel" type="text" disabled />
      </div>
      <div class="field-row flex gap-10">
        <div class="field w-half">
          <label>From</label>
          <DateRangePicker
            class="date-picker"
            :timePicker="true"
            :singleDatePicker="true"
            :autoApply="true"
            :append-to-body="true"
            opens="right"
            v-model="fromPicker"
            @update="onFromUpdate"
          >
            <template v-slot:input>
              {{ fromLabel }}
            </template>
          </DateRangePicker>
          <button v-if="form.valid_from" type="button" class="clear-date" @click="clearFrom">Clear</button>
        </div>
        <div class="field w-half">
          <label>To</label>
          <DateRangePicker
            class="date-picker"
            :timePicker="true"
            :singleDatePicker="true"
            :autoApply="true"
            :append-to-body="true"
            opens="right"
            v-model="toPicker"
            @update="onToUpdate"
          >
            <template v-slot:input>
              {{ toLabel }}
            </template>
          </DateRangePicker>
          <button v-if="form.valid_to" type="button" class="clear-date" @click="clearTo">Clear</button>
        </div>
      </div>

      <div class="section-title mt-20">Restriction Parameters</div>
      <div class="field unit">
        <label>Weight</label>
        <div class="input-with-unit">
          <input v-model="form.weight_min" type="number" step="0.01" min="0" />
          <span>t</span>
        </div>
      </div>
      <div class="field unit">
        <label>Width</label>
        <div class="input-with-unit">
          <input v-model="form.width_min" type="number" step="0.01" min="0" />
          <span>m</span>
        </div>
      </div>
      <div class="field unit">
        <label>Height</label>
        <div class="input-with-unit">
          <input v-model="form.height_min" type="number" step="0.01" min="0" />
          <span>m</span>
        </div>
      </div>
      <div class="field unit">
        <label>Length</label>
        <div class="input-with-unit">
          <input v-model="form.length_min" type="number" step="0.01" min="0" />
          <span>m</span>
        </div>
      </div>

      <div class="form-actions flex justify-between mt-20">
        <router-link class="btn bg-grey-lighter" :to="{ name: 'AccountRoadExclusions' }">Cancel</router-link>
        <button class="btn btn-primary" type="button" :disabled="saving" @click="save">Save</button>
      </div>
    </div>

    <div class="map-panel">
      <div class="map-search">
        <div class="search-input-wrap flex items-center">
          <input
            v-model="searchQuery"
            type="text"
            class="search-input"
            placeholder="Search address..."
            autocomplete="off"
            @input="onSearchInput"
            @keyup.enter="applySearch"
          />
          <img
            class="search-icon"
            src="@/assets/images/search.svg"
            alt="Search"
            @click="applySearch"
          />
        </div>
        <ul v-if="searchQuery && suggestions.length" class="suggestions">
          <li
            v-for="(s, idx) in suggestions"
            :key="idx"
            @click="selectSuggestion(s)"
          >
            {{ s.address }}
          </li>
        </ul>
      </div>
      <div ref="mapContainer" class="map-container"></div>
    </div>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import moment from 'moment';
import { infoMessage } from '@/services/messages';
import pinIconUrl from '@/assets/images/pin.svg';

export default {
  name: 'RoadExclusionForm',
  components: { DateRangePicker },
  props: {
    id: { type: [String, Number], default: null },
  },
  data() {
    return {
      saving: false,
      nameTouchedManually: false,
      form: {
        name: '',
        name_is_manual: false,
        segment_id: '',
        segment_length: null,
        segment_polyline: null,
        segment_geometry: null,
        click_lat: null,
        click_lng: null,
        valid_from: null,
        valid_to: null,
        weight_min: null,
        width_min: null,
        height_min: null,
        length_min: null,
        is_active: true,
      },
      fromPicker: { startDate: null, endDate: null },
      toPicker: { startDate: null, endDate: null },
      searchQuery: '',
      selectedSearch: null,
      suggestions: [],
      searchTimer: null,
      map: null,
      platform: null,
      behavior: null,
      marker: null,
      segmentLine: null,
    };
  },
  computed: {
    isEdit() {
      return !!this.id || this.$route.name === 'EditAccountRoadExclusion';
    },
    editId() {
      return this.id || this.$route.params.id;
    },
    lengthLabel() {
      if (this.form.segment_length == null || this.form.segment_length === '') {
        return '';
      }
      return Number(this.form.segment_length).toFixed(1) + ' m';
    },
    fromLabel() {
      return this.form.valid_from
        ? moment(this.form.valid_from).format('DD/MM/YYYY HH:mm')
        : '--/--/----';
    },
    toLabel() {
      return this.form.valid_to
        ? moment(this.form.valid_to).format('DD/MM/YYYY HH:mm')
        : '--/--/----';
    },
  },
  async mounted() {
    try {
      await this.loadHereMapsAPI();
      await this.initMap();
      if (this.isEdit) {
        await this.loadExisting();
      }
    } catch (e) {
      console.error(e);
      infoMessage('Failed to load map', 'danger');
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
    if (this.map) {
      this.map.dispose();
      this.map = null;
    }
  },
  methods: {
    apiBase() {
      return this.$http.apiUrl() + '/road-exclusions';
    },
    onNameInput() {
      this.nameTouchedManually = true;
      this.form.name_is_manual = true;
    },
    onFromUpdate(val) {
      this.form.valid_from = val && val.startDate ? new Date(val.startDate).toISOString() : null;
    },
    onToUpdate(val) {
      this.form.valid_to = val && val.startDate ? new Date(val.startDate).toISOString() : null;
    },
    clearFrom() {
      this.form.valid_from = null;
      this.fromPicker = { startDate: null, endDate: null };
    },
    clearTo() {
      this.form.valid_to = null;
      this.toPicker = { startDate: null, endDate: null };
    },
    ensureHereCss() {
      const href = 'https://js.api.here.com/v3/3.2/mapsjs-ui.css';
      if (document.querySelector(`link[href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = href;
      document.head.appendChild(link);
    },
    loadScript(src) {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          if (window.H && window.H.Map) return resolve();
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', reject);
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async loadHereMapsAPI() {
      if (window.H && window.H.Map) {
        return;
      }
      this.ensureHereCss();
      const scripts = [
        'https://js.api.here.com/v3/3.2/mapsjs-core.js',
        'https://js.api.here.com/v3/3.2/mapsjs-mapevents.js',
        'https://js.api.here.com/v3/3.2/mapsjs-service.js',
        'https://js.api.here.com/v3/3.2/mapsjs-ui.js',
      ];
      for (const src of scripts) {
        await this.loadScript(src);
      }
      if (!window.H || !window.H.Map) {
        throw new Error('HERE Maps API failed to load');
      }
    },
    async initMap() {
      if (!this.$refs.mapContainer) {
        throw new Error('Map container not found');
      }
      if (!process.env.VUE_APP_HERE_API_KEY) {
        throw new Error('HERE API key is not configured (VUE_APP_HERE_API_KEY)');
      }
      this.platform = new H.service.Platform({
        apikey: process.env.VUE_APP_HERE_API_KEY,
      });
      const engineType = H.Map.EngineType.HARP;
      const defaultLayers = this.platform.createDefaultLayers({ engineType });
      const baseLayer =
        (defaultLayers.vector.normal && defaultLayers.vector.normal.logistics) ||
        defaultLayers.vector.normal.map;
      this.map = new H.Map(this.$refs.mapContainer, baseLayer, {
        zoom: 6,
        center: { lat: 51.5074, lng: -0.1278 },
        engineType,
      });
      this.behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
      if (window.H && H.ui) {
        H.ui.UI.createDefault(this.map, defaultLayers);
      }
      window.addEventListener('resize', this.onWindowResize);
      await this.$nextTick();
      this.scheduleMapResize();
      this.map.addEventListener('tap', this.onMapTap);
    },
    scheduleMapResize() {
      requestAnimationFrame(() => {
        if (this.map) this.map.getViewPort().resize();
        setTimeout(() => {
          if (this.map) this.map.getViewPort().resize();
        }, 100);
      });
    },
    onWindowResize() {
      if (this.map) this.map.getViewPort().resize();
    },
    async onMapTap(evt) {
      const coord = this.map.screenToGeo(
        evt.currentPointer.viewportX,
        evt.currentPointer.viewportY,
      );
      await this.resolveAndDraw(coord.lat, coord.lng);
    },
    async resolveAndDraw(lat, lng) {
      try {
        const response = await this.$http.postAuth(
          this.apiBase() + '/resolve-segment',
          { lat, lng },
        );
        const data = response.data || {};
        this.form.segment_id = data.segment_id || '';
        this.form.segment_length = data.length_m != null ? data.length_m : null;
        this.form.segment_polyline = data.segment_polyline || null;
        this.form.segment_geometry = data.segment_geometry || null;
        this.form.click_lat = lat;
        this.form.click_lng = lng;
        if (!this.form.name_is_manual) {
          const nameHint = data.name_hint || 'Road segment';
          const len =
            data.length_m != null ? Number(data.length_m).toFixed(1) + 'm' : '';
          this.form.name =
            nameHint + ' - ' + data.segment_id + (len ? ' - ' + len : '');
        }
        this.drawPin(lat, lng);
        this.drawSegmentGeometry(data.segment_polyline, data.segment_geometry);
      } catch (e) {
        const status = e.response && e.response.status;
        const url = this.apiBase() + '/resolve-segment';
        if (status === 404) {
          console.error('resolve-segment 404:', url);
          infoMessage(
            'Road segment API not found — check VUE_APP_ROOT_API points to NestJS backend (port 3002)',
            'danger',
          );
        } else {
          infoMessage('Could not resolve road segment', 'danger');
        }
      }
    },
    createPinIcon() {
      return new H.map.Icon(pinIconUrl, {
        size: { w: 30, h: 36 },
        anchor: { x: 15, y: 36 },
      });
    },
    drawPin(lat, lng) {
      if (this.marker) {
        this.map.removeObject(this.marker);
        this.marker = null;
      }
      this.marker = new H.map.Marker({ lat, lng }, { icon: this.createPinIcon() });
      this.map.addObject(this.marker);
    },
    lineStringFromGeometry(geometry) {
      const coords = geometry && geometry.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return null;
      const linestring = new H.geo.LineString();
      coords.forEach((pair) => {
        if (!Array.isArray(pair) || pair.length < 2) return;
        const lng = Number(pair[0]);
        const lat = Number(pair[1]);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        linestring.pushPoint({ lat, lng });
      });
      return linestring.getPointCount() >= 2 ? linestring : null;
    },
    drawSegmentGeometry(polyline, geometry) {
      if (this.segmentLine) {
        this.map.removeObject(this.segmentLine);
        this.segmentLine = null;
      }
      try {
        let linestring = null;
        if (polyline) {
          linestring = H.geo.LineString.fromFlexiblePolyline(polyline);
        } else {
          linestring = this.lineStringFromGeometry(geometry);
        }
        if (!linestring || linestring.getPointCount() < 2) return;
        this.segmentLine = new H.map.Polyline(linestring, {
          style: { strokeColor: '#FF0000', lineWidth: 8 },
        });
        this.map.addObject(this.segmentLine);
      } catch (e) {
        console.error(e);
      }
    },
    async refreshSegmentGeometry(segmentId) {
      if (!segmentId) return null;
      try {
        const response = await this.$http.getAuth(
          this.apiBase() + '/segment-geometry/' + encodeURIComponent(segmentId),
        );
        return response.data || null;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    isValidSearchString(str) {
      return /(?=(?:\S*\w){3})/gmi.test(str || '');
    },
    getMapCenterAt() {
      if (!this.map) return null;
      const center = this.map.getCenter();
      return { lat: center.lat, lng: center.lng };
    },
    mapSearchResults(data) {
      const list = Array.isArray(data) ? data : [];
      return list
        .filter((item) => item?.position?.lat != null && item?.position?.lng != null)
        .map((item) => ({
          address: (item.address && item.address.label) || item.title || '',
          lat: item.position.lat,
          lng: item.position.lng,
        }))
        .filter((item) => item.address);
    },
    async requestSearchResults(query) {
      const payload = { string: query };
      const at = this.getMapCenterAt();
      if (at) payload.at = at;
      const response = await this.$http.post(
        this.$http.apiUrl() + '/subscriptions/search-points',
        payload,
      );
      return this.mapSearchResults(response.data);
    },
    onSearchInput() {
      this.selectedSearch = null;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null;
        this.fetchSuggestions();
      }, 500);
    },
    async fetchSuggestions() {
      const q = (this.searchQuery || '').trim();
      if (!this.isValidSearchString(q)) {
        this.suggestions = [];
        return;
      }
      try {
        this.suggestions = await this.requestSearchResults(q);
      } catch (e) {
        console.error(e);
        this.suggestions = [];
        infoMessage('Address search failed', 'danger');
      }
    },
    selectSuggestion(s) {
      this.searchQuery = s.address;
      this.selectedSearch = { address: s.address, lat: s.lat, lng: s.lng };
      this.suggestions = [];
    },
    async applySearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      const q = (this.searchQuery || '').trim();
      if (!q) return;
      if (
        this.selectedSearch &&
        this.selectedSearch.address === q &&
        this.selectedSearch.lat != null &&
        this.selectedSearch.lng != null
      ) {
        await this.flyToLocation(this.selectedSearch.lat, this.selectedSearch.lng);
        this.suggestions = [];
        return;
      }
      if (!this.isValidSearchString(q)) {
        infoMessage('Enter at least 3 characters to search', 'danger');
        return;
      }
      try {
        const results = await this.requestSearchResults(q);
        if (!results.length) {
          infoMessage('Address not found', 'danger');
          return;
        }
        const match = results[0];
        this.searchQuery = match.address;
        this.selectedSearch = match;
        this.suggestions = [];
        await this.flyToLocation(match.lat, match.lng);
      } catch (e) {
        infoMessage('Address search failed', 'danger');
      }
    },
    async flyToLocation(lat, lng) {
      if (!this.map) return;
      const position = { lat, lng };
      this.map.getViewModel().setLookAtData({ position, zoom: 14 }, false);
      await this.$nextTick();
      this.map.getViewPort().resize();
    },
    async loadExisting() {
      try {
        const response = await this.$http.getAuth(
          this.apiBase() + '/' + this.editId,
        );
        const item = response.data && response.data.item;
        if (!item) return;
        this.form = {
          name: item.name,
          name_is_manual: !!item.name_is_manual,
          segment_id: item.segment_id,
          segment_length: item.segment_length,
          segment_polyline: item.segment_polyline,
          segment_geometry: item.segment_geometry,
          click_lat: item.click_lat,
          click_lng: item.click_lng,
          valid_from: item.valid_from,
          valid_to: item.valid_to,
          weight_min: item.weight_min,
          width_min: item.width_min,
          height_min: item.height_min,
          length_min: item.length_min,
          is_active: item.is_active,
        };
        this.nameTouchedManually = !!item.name_is_manual;
        if (item.click_lat != null && item.click_lng != null) {
          this.drawPin(item.click_lat, item.click_lng);
        }
        const refreshed = await this.refreshSegmentGeometry(item.segment_id);
        if (refreshed && (refreshed.segment_polyline || refreshed.segment_geometry)) {
          this.form.segment_polyline = refreshed.segment_polyline || item.segment_polyline;
          this.form.segment_geometry =
            refreshed.segment_geometry || item.segment_geometry;
          if (refreshed.length_m != null) {
            this.form.segment_length = refreshed.length_m;
          }
        }
        this.drawSegmentGeometry(
          this.form.segment_polyline,
          this.form.segment_geometry,
        );
        if (
          !this.form.segment_polyline &&
          !this.form.segment_geometry &&
          item.click_lat != null &&
          item.click_lng != null
        ) {
          this.map.setCenter({ lat: item.click_lat, lng: item.click_lng });
          this.map.setZoom(16);
        }
      } catch (e) {
        infoMessage('Failed to load road exclusion', 'danger');
      }
    },
    nullIfEmpty(val) {
      if (val === '' || val === null || val === undefined) return null;
      const n = Number(val);
      return Number.isNaN(n) ? null : n;
    },
    async save() {
      const name = (this.form.name || '').trim();
      if (!name || !this.form.segment_id || this.form.segment_length == null) {
        infoMessage('Name, Segment ID and Segment length are required', 'danger');
        return;
      }
      this.saving = true;
      const payload = {
        name,
        name_is_manual: !!this.form.name_is_manual,
        segment_id: String(this.form.segment_id),
        segment_length: Number(this.form.segment_length),
        segment_polyline: this.form.segment_polyline,
        segment_geometry: this.form.segment_geometry,
        click_lat: this.form.click_lat,
        click_lng: this.form.click_lng,
        valid_from: this.form.valid_from,
        valid_to: this.form.valid_to,
        weight_min: this.nullIfEmpty(this.form.weight_min),
        width_min: this.nullIfEmpty(this.form.width_min),
        height_min: this.nullIfEmpty(this.form.height_min),
        length_min: this.nullIfEmpty(this.form.length_min),
        is_active: this.form.is_active !== false,
      };
      try {
        if (this.isEdit) {
          const axios = require('axios');
          await axios.put(this.apiBase() + '/' + this.editId, payload, {
            headers: this.$http.getAuthHeader(),
          });
        } else {
          await this.$http.postAuth(this.apiBase(), payload);
        }
        infoMessage('Road exclusion saved', 'success');
        this.$router.push({ name: 'AccountRoadExclusions' });
      } catch (e) {
        infoMessage('Save failed', 'danger');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page.road-exclusion-form {
  width: 100%;
  min-height: calc(100vh - 280px);
  min-width: 0;
}
.form-panel {
  width: 290px;
  max-width: 290px;
  flex-shrink: 0;
  padding: 20px;
  text-align: left;
  overflow-y: auto;
}
.form-title {
  font-family: Catamaran, sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: #22292F;
  margin: 0 0 20px;
}
.section-title {
  font-family: Catamaran, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 18px;
  color: #22292F;
  margin-bottom: 12px;
}
.field {
  margin-bottom: 12px;
  label {
    display: block;
    margin-bottom: 4px;
    color: #303741;
  }
  input {
    width: 100%;
    border: 1px solid #E2E4E6;
    border-radius: 8px;
    padding: 8px 10px;
  }
  input:disabled {
    background: #F5F7F8;
    color: #969A9F;
  }
}
.input-with-unit {
  display: flex;
  align-items: center;
  gap: 8px;
  input { flex: 1; }
  span { color: #969A9F; }
}
.map-panel {
  flex: 1;
  position: relative;
  min-width: 0;
  align-self: stretch;
  min-height: 600px;
}
.map-container {
  width: 100%;
  height: 100%;
  min-height: 600px;
  position: relative;
}
.map-search {
  position: absolute;
  z-index: 2;
  top: 16px;
  left: 16px;
  right: 80px;
  .search-input-wrap {
    background: #fff;
    border-radius: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,.12);
    padding: 0 12px 0 16px;
  }
  .search-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    padding: 12px 8px 12px 0;
    font-family: Catamaran, sans-serif;
    font-size: 14px;
    color: #303741;
    &::placeholder { color: #969A9F; }
  }
  .search-icon {
    width: 18px;
    height: 18px;
    cursor: pointer;
    flex-shrink: 0;
  }
  .suggestions {
    list-style: none;
    margin: 4px 0 0;
    padding: 0;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,.12);
    max-height: 220px;
    overflow-y: auto;
    li {
      padding: 10px 14px;
      cursor: pointer;
      text-align: left;
      &:hover { background: #F5F7F8; }
    }
  }
}
.w-half { width: 50%; }
.gap-10 { gap: 10px; }
.mt-20 { margin-top: 20px; }
.clear-date {
  margin-top: 4px;
  font-size: 12px;
  color: #2468CA;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.date-picker {
  width: 100%;
}
.form-actions {
  gap: 12px;
  .btn {
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    border: 1px solid #dae1e7;
    border-radius: 0.25rem;
    padding: 0.5rem 1rem;
    cursor: pointer;
    color: #22292f;
  }
  .bg-grey-lighter {
    background-color: #f1f5f8;
  }
  .btn-primary {
    color: #fff;
    background-color: #1FBC2F;
    border-color: #48a77c;
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>

<!-- append-to-body calendar lives outside this component; keep it above the map -->
<style lang="scss">
.daterangepicker {
  z-index: 3000;
}
</style>
